import {
  collection,
  doc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase';

const STATUS_TO_PIPELINE = {
  novo: 'lead',
  qualificado: 'qualificado',
  proposta: 'proposta',
  negociacao: 'negociacao',
  ganho: 'ganho',
  perdido: 'perdido'
};

function leadStatusToPipelineColumn(status) {
  if (!status) return 'lead';
  const s = String(status).toLowerCase().trim();
  if (s === 'novo' || s === 'lead') return 'lead';
  return STATUS_TO_PIPELINE[s] || status;
}

function normalizePropertyCodes(codes) {
  if (!codes) return [];
  if (Array.isArray(codes)) return codes;
  if (typeof codes === 'string') return codes.split(',').map((c) => c.trim()).filter(Boolean);
  return [];
}

function buildCardDataFromLead(leadData, leadId, pipelineStatus) {
  return {
    leadId,
    name: leadData.name,
    email: leadData.email || '',
    phone: leadData.phone || '',
    propertyInterest: leadData.propertyInterest || '',
    propertyTitle: leadData.propertyTitle || '',
    propertyLink: leadData.propertyLink || '',
    value: leadData.value ?? null,
    notes: leadData.notes || '',
    corretorAtendendo: leadData.corretorAtendendo || '',
    company: leadData.company || '',
    type: leadData.type || 'help',
    propertyCode: normalizePropertyCodes(leadData.propertyCode),
    descricao: leadData.descricao || '',
    adminNotes: leadData.adminNotes || '',
    leadHeatOverride: leadData.leadHeatOverride || null,
    lossReason: leadData.lossReason || null,
    visitDate: leadData.visitDate || '',
    visitTime: leadData.visitTime || '',
    visitAddress: leadData.visitAddress || '',
    status: pipelineStatus,
    source: leadData.source || 'manual',
    history: leadData.history || [],
    stageHistory: leadData.stageHistory || [],
    updatedAt: serverTimestamp()
  };
}

function getLeadSortTime(lead) {
  const raw = lead.updatedAt || lead.createdAt;
  if (raw?.toDate) return raw.toDate().getTime();
  if (raw) return new Date(raw).getTime();
  return 0;
}

function getCardSortTime(card) {
  const raw = card.updatedAt || card.createdAt;
  if (raw?.toDate) return raw.toDate().getTime();
  if (raw) return new Date(raw).getTime();
  return 0;
}

/**
 * Sincroniza um lead para um card do pipeline (1:1 por leadId — sem agrupar por telefone/e-mail).
 */
export async function syncLeadToPipeline(leadData, leadId) {
  const resolvedLeadId = leadId || leadData?.id;
  if (!resolvedLeadId) {
    console.error('syncLeadToPipeline: leadId obrigatório');
    return;
  }

  try {
    const pipelineStatus = leadStatusToPipelineColumn(leadData.status);
    const cardData = buildCardDataFromLead(leadData, resolvedLeadId, pipelineStatus);

    await runTransaction(db, async (transaction) => {
      const cardsQuery = query(
        collection(db, 'pipeline'),
        where('leadId', '==', resolvedLeadId)
      );
      const cardSnapshot = await transaction.get(cardsQuery);

      if (!cardSnapshot.empty) {
        const existingCard = cardSnapshot.docs[0];
        transaction.update(existingCard.ref, {
          ...cardData,
          order: existingCard.data().order ?? 0
        });
        return;
      }

      const statusQuery = query(
        collection(db, 'pipeline'),
        where('status', '==', pipelineStatus)
      );
      const statusSnapshot = await transaction.get(statusQuery);
      const newCardRef = doc(collection(db, 'pipeline'));

      transaction.set(newCardRef, {
        ...cardData,
        order: statusSnapshot.size,
        createdAt: serverTimestamp()
      });
    });
  } catch (error) {
    console.error('Erro ao sincronizar lead para pipeline:', error);
  }
}

/**
 * Vincula um card existente (sem leadId) a um lead — evita duplicar cards na migração.
 */
async function linkOrphanCardToLead(cardId, existingCard, leadData, leadId) {
  const pipelineStatus = leadStatusToPipelineColumn(leadData.status);
  const cardData = buildCardDataFromLead(leadData, leadId, pipelineStatus);

  await updateDoc(doc(db, 'pipeline', cardId), {
    ...cardData,
    order: existingCard.order ?? 0
  });
}

/**
 * Cria cards no pipeline para leads que ainda não têm vínculo por leadId (1:1, sem agrupar).
 */
export async function syncAllLeadsMissingInPipeline() {
  try {
    const [leadsSnap, pipelineSnap] = await Promise.all([
      getDocs(collection(db, 'leads')),
      getDocs(collection(db, 'pipeline'))
    ]);

    const linkedLeadIds = new Set(
      pipelineSnap.docs.map((d) => d.data().leadId).filter(Boolean)
    );

    const missingLeads = leadsSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((lead) => !linkedLeadIds.has(lead.id))
      .sort((a, b) => getLeadSortTime(b) - getLeadSortTime(a));

    const orphanCards = pipelineSnap.docs
      .filter((d) => !d.data().leadId)
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => getCardSortTime(b) - getCardSortTime(a));

    let linkedOrphans = 0;
    let created = 0;
    let orphanIndex = 0;

    for (const lead of missingLeads) {
      if (orphanIndex < orphanCards.length) {
        const orphan = orphanCards[orphanIndex++];
        await linkOrphanCardToLead(orphan.id, orphan, lead, lead.id);
        linkedOrphans++;
      } else {
        await syncLeadToPipeline(lead, lead.id);
        created++;
      }
    }

    return {
      leadsTotal: leadsSnap.size,
      pipelineBefore: pipelineSnap.size,
      linkedOrphans,
      created,
      synced: missingLeads.length
    };
  } catch (error) {
    console.error('Erro ao sincronizar leads ausentes no pipeline:', error);
    throw error;
  }
}
